"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useRange } from "react-instantsearch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { meilisearchClient } from "@/lib/meilisearch";
import { INDEXES } from "@/lib/meilisearch";

interface BudgetFilterInstantSearchProps {
  attribute: string;
}

interface BudgetFilterManualProps {
  budgetMin?: number | null;
  budgetMax?: number | null;
  onBudgetChange: (min: number | null, max: number | null) => void;
  indexName?: string;
  includeExpired?: boolean;
}

// Budget Filter component for InstantSearch mode (creators)
function BudgetFilterInstantSearch({ attribute }: BudgetFilterInstantSearchProps) {
  const instantSearchRange = useRange({ attribute });

  const [minValue, setMinValue] = useState<string>("");
  const [maxValue, setMaxValue] = useState<string>("");
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);
  const [rangeBounds, setRangeBounds] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from InstantSearch range
  useEffect(() => {
    const { start, range } = instantSearchRange;
    const isRangeLoaded =
      range.min !== undefined &&
      range.min !== null &&
      range.max !== undefined &&
      range.max !== null;

    if (isRangeLoaded) {
      const minBound = range.min ?? 0;
      const maxBound = range.max ?? 10000;
      const currentMin =
        start[0] !== undefined && start[0] !== null ? start[0] : minBound;
      const currentMax =
        start[1] !== undefined && start[1] !== null ? start[1] : maxBound;

      if (!isEditingMin && !isEditingMax) {
        setMinValue(currentMin.toString());
        setMaxValue(currentMax.toString());
      }
      setRangeBounds({ min: minBound, max: maxBound });
      setIsLoading(false);
    }
  }, [
    instantSearchRange?.range,
    instantSearchRange?.start,
    isEditingMin,
    isEditingMax,
  ]);


  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsEditingMin(true);
    setMinValue(value);

    if (value === "") {
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return;

    const { start, range } = instantSearchRange;
    const maxBound = range.max ?? 10000;
    const max = start[1] !== undefined ? start[1] : maxBound;
    instantSearchRange.refine([numValue, max]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsEditingMax(true);
    setMaxValue(value);

    if (value === "") {
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return;

    const { start, range } = instantSearchRange;
    const minBound = range.min ?? 0;
    const min = start[0] !== undefined ? start[0] : minBound;
    instantSearchRange.refine([min, numValue]);
  };

  const handleMinFocus = () => {
    setIsEditingMin(true);
  };

  const handleMaxFocus = () => {
    setIsEditingMax(true);
  };

  const handleBlur = () => {
    setIsEditingMin(false);
    setIsEditingMax(false);

    if (!rangeBounds) return;

    // Ensure values are within bounds and min <= max
    let min = Number(minValue) || rangeBounds.min;
    let max = Number(maxValue) || rangeBounds.max;

    if (min < rangeBounds.min) min = rangeBounds.min;
    if (max > rangeBounds.max) max = rangeBounds.max;
    if (min > max) {
      const temp = min;
      min = max;
      max = temp;
    }

    setMinValue(min.toString());
    setMaxValue(max.toString());
    instantSearchRange.refine([min, max]);
  };

  // Show loading state
  if (isLoading || !rangeBounds) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">
            Budget Range
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="budget-min" className="text-xs text-slate-600 mb-1.5 block">
                Min
              </Label>
              <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
            </div>
            <div className="flex-1">
              <Label htmlFor="budget-max" className="text-xs text-slate-600 mb-1.5 block">
                Max
              </Label>
              <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">
          Budget Range
        </h3>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label htmlFor="budget-min" className="text-xs text-slate-600 mb-1.5 block">
              Min
            </Label>
            <Input
              id="budget-min"
              type="number"
              value={minValue}
              onChange={handleMinChange}
              onFocus={handleMinFocus}
              onBlur={handleBlur}
              min={rangeBounds.min}
              max={rangeBounds.max}
              placeholder={rangeBounds.min.toString()}
              className="w-full"
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="budget-max" className="text-xs text-slate-600 mb-1.5 block">
              Max
            </Label>
            <Input
              id="budget-max"
              type="number"
              value={maxValue}
              onChange={handleMaxChange}
              onFocus={handleMaxFocus}
              onBlur={handleBlur}
              min={rangeBounds.min}
              max={rangeBounds.max}
              placeholder={rangeBounds.max.toString()}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Budget Filter component for manual mode (campaigns)
function BudgetFilterManual({
  budgetMin: externalBudgetMin,
  budgetMax: externalBudgetMax,
  onBudgetChange,
  indexName,
  includeExpired = false,
}: BudgetFilterManualProps) {
  const [minValue, setMinValue] = useState<string>("");
  const [maxValue, setMaxValue] = useState<string>("");
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);
  const [rangeBounds, setRangeBounds] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load budget range from Meilisearch for campaigns
  useEffect(() => {
    loadBudgetRange();
  }, [indexName, includeExpired]);

  const loadBudgetRange = async () => {
    try {
      setIsLoading(true);
      const index = meilisearchClient.index(indexName || INDEXES.CAMPAIGNS);

      // Get all campaigns to calculate min/max budget
      const allCampaignsResults = await index.search("", {
        limit: 1000,
      });
      let allCampaigns = allCampaignsResults.hits || [];

      // Filter to only non-expired campaigns unless we explicitly include expired
      if (!includeExpired) {
        const currentDate = new Date().toISOString().split("T")[0];
        allCampaigns = allCampaigns.filter((campaign: any) => {
          if (!campaign.applicationDeadline) return true;
          return campaign.applicationDeadline >= currentDate;
        });
      }

      // Calculate min and max budget
      const budgets = allCampaigns
        .map((campaign: any) => campaign.budget)
        .filter((budget: any) => budget !== undefined && budget !== null && typeof budget === "number");

      if (budgets.length > 0) {
        const min = Math.min(...budgets);
        const max = Math.max(...budgets);
        setRangeBounds({ min, max });

        // Initialize values if not set
        if (externalBudgetMin === undefined && externalBudgetMax === undefined) {
          setMinValue(min.toString());
          setMaxValue(max.toString());
        } else {
          setMinValue(
            externalBudgetMin !== null && externalBudgetMin !== undefined
              ? externalBudgetMin.toString()
              : min.toString()
          );
          setMaxValue(
            externalBudgetMax !== null && externalBudgetMax !== undefined
              ? externalBudgetMax.toString()
              : max.toString()
          );
        }
      } else {
        // Fallback values
        setRangeBounds({ min: 0, max: 100000 });
        setMinValue("0");
        setMaxValue("100000");
      }
    } catch (error) {
      console.error("Failed to load budget range:", error);
      // Fallback values
      setRangeBounds({ min: 0, max: 100000 });
      setMinValue("0");
      setMaxValue("100000");
    } finally {
      setIsLoading(false);
    }
  };

  // Sync with external values
  useEffect(() => {
    if (externalBudgetMin !== undefined && !isEditingMin) {
      setMinValue(
        externalBudgetMin !== null ? externalBudgetMin.toString() : ""
      );
    }
    if (externalBudgetMax !== undefined && !isEditingMax) {
      setMaxValue(
        externalBudgetMax !== null ? externalBudgetMax.toString() : ""
      );
    }
  }, [externalBudgetMin, externalBudgetMax, isEditingMin, isEditingMax]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsEditingMin(true);
    setMinValue(value);

    if (value === "") {
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return;

    if (onBudgetChange && rangeBounds) {
      const max =
        externalBudgetMax !== null && externalBudgetMax !== undefined
          ? externalBudgetMax
          : rangeBounds.max;
      onBudgetChange(numValue, max);
    }
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsEditingMax(true);
    setMaxValue(value);

    if (value === "") {
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return;

    if (onBudgetChange && rangeBounds) {
      const min =
        externalBudgetMin !== null && externalBudgetMin !== undefined
          ? externalBudgetMin
          : rangeBounds.min;
      onBudgetChange(min, numValue);
    }
  };

  const handleMinFocus = () => {
    setIsEditingMin(true);
  };

  const handleMaxFocus = () => {
    setIsEditingMax(true);
  };

  const handleBlur = () => {
    setIsEditingMin(false);
    setIsEditingMax(false);

    if (!rangeBounds) return;

    // Ensure values are within bounds and min <= max
    let min = Number(minValue) || rangeBounds.min;
    let max = Number(maxValue) || rangeBounds.max;

    if (min < rangeBounds.min) min = rangeBounds.min;
    if (max > rangeBounds.max) max = rangeBounds.max;
    if (min > max) {
      const temp = min;
      min = max;
      max = temp;
    }

    setMinValue(min.toString());
    setMaxValue(max.toString());

    if (onBudgetChange) {
      onBudgetChange(min, max);
    }
  };

  // Show loading state
  if (isLoading || !rangeBounds) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">
            Budget Range
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="budget-min" className="text-xs text-slate-600 mb-1.5 block">
                Min
              </Label>
              <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
            </div>
            <div className="flex-1">
              <Label htmlFor="budget-max" className="text-xs text-slate-600 mb-1.5 block">
                Max
              </Label>
              <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">
          Budget Range
        </h3>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label htmlFor="budget-min" className="text-xs text-slate-600 mb-1.5 block">
              Min
            </Label>
            <Input
              id="budget-min"
              type="number"
              value={minValue}
              onChange={handleMinChange}
              onFocus={handleMinFocus}
              onBlur={handleBlur}
              min={rangeBounds.min}
              max={rangeBounds.max}
              placeholder={rangeBounds.min.toString()}
              className="w-full"
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="budget-max" className="text-xs text-slate-600 mb-1.5 block">
              Max
            </Label>
            <Input
              id="budget-max"
              type="number"
              value={maxValue}
              onChange={handleMaxChange}
              onFocus={handleMaxFocus}
              onBlur={handleBlur}
              min={rangeBounds.min}
              max={rangeBounds.max}
              placeholder={rangeBounds.max.toString()}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Main BudgetFilter component that chooses between InstantSearch and manual mode
export function BudgetFilter(
  props: BudgetFilterInstantSearchProps | BudgetFilterManualProps
) {
  // If attribute is provided, use InstantSearch mode
  if ("attribute" in props && props.attribute) {
    return <BudgetFilterInstantSearch attribute={props.attribute} />;
  }

  // Otherwise, use manual mode
  return <BudgetFilterManual {...(props as BudgetFilterManualProps)} />;
}

